<template>
  <div>
    <v-dialog
      v-model="dialog"
      persistent
      fullscreen
      dark
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            Scan Barcode
          </v-toolbar-title>

          <v-spacer></v-spacer>

          <v-btn depressed rounded text @click="closeScanner">
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </v-toolbar>

        <div
          id="bar-code-reader"
          style="width:100%; height: 100%;"
        ></div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'

export default {
  name: 'BaseBarcodeReader',
  props: {
    isValidResult: {
      type: Function,
      default: decodedText => /^[a-zA-Z0-9-]+$/.test(decodedText),
    },
    invalidResultMessage: {
      type: String,
      default:
        'ข้อมูลไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง หรือพิมพ์ในช่องกรอกข้อมูล',
    },
  },
  mounted() {
    // Safari (esp. iOS) restores this page from the back/forward cache when the
    // user taps Back after a window.location redirect. mounted() does NOT run
    // again on a bfcache restore — `pageshow` is the only event that fires — so a
    // scanner modal that was open when the user left is otherwise frozen open.
    // Do NOT gate on event.persisted: iOS Safari often reports it false for a
    // genuine bfcache restore.
    window.addEventListener('pageshow', this.handlePageShow)
  },
  data() {
    return {
      dialog: false,
      html5QrCode: null,
      index: 0,
    }
  },
  computed: {
    isHtml5Barcode() {
      return !!this.html5Barcode || false
    },
  },
  methods: {
    openScanner(event) {
      this.dialog = true
      this.index = event ?? 0

      setTimeout(() => {
        this.startScanner()
      }, 1000)
    },
    closeDialog() {
      this.dialog = false
      this.index = 0
    },
    closeScanner() {
      this.closeDialog()

      // Ensure to cleanup and stop the scanner when the component is destroyed
      if (this.isHtml5Barcode) {
        this.html5Barcode.stop().catch(() => {
          //   Unable to stop scanning
          alert('เกิดข้อผิดพลาดในการหยุดการสแกน')
        })
      }
    },
    handlePageShow() {
      // Only act when we come back to a page that still thinks the scanner is
      // open (a fresh initial load has dialog === false, so this is a no-op).
      if (this.dialog) {
        this.forceCloseScanner()
      }
    },
    forceCloseScanner() {
      // Like closeScanner() but silent: after a bfcache restore the camera stream
      // is already gone, so stop() rejects — we must not pop the error alert.
      this.closeDialog()

      const scanner = this.html5Barcode
      if (!scanner || typeof scanner.stop !== 'function') {
        return
      }
      try {
        const stopping = scanner.stop()
        if (stopping && typeof stopping.catch === 'function') {
          stopping.catch(() => {})
        }
      } catch (e) {
        // scanner already stopped / torn down — nothing to do
      }
      this.html5Barcode = null
    },

    async startScanner() {
      // Create instance of the scanner
      this.html5Barcode = new Html5Qrcode('bar-code-reader')
      const config = {
        fps: 10,
        qrbox: { width: 320, height: 320 },
      }

      if (this.isHtml5Barcode) {
        try {
          const cameras = await Html5Qrcode.getCameras()
          if (cameras && cameras.length > 0) {
            // Start scanning
            this.html5Barcode.start(
              { facingMode: { exact: 'environment' } },
              config,
              // eslint-disable-next-line no-unused-vars
              (decodedText, decodedResult) => {
                this.html5Barcode
                  .stop()
                  .then(() => {
                    if (!this.isValidResult(decodedText)) {
                      this.$toast.error(this.invalidResultMessage)
                      return
                    }

                    this.$emit('result', decodedText, this.index)

                    this.closeDialog()
                  })
                  .catch(() => {
                    // Failed to stop the scanner
                    this.closeDialog()
                    alert('เกิดข้อผิดพลาดในการหยุดการสแกน')
                  })
              },
            )
          } else {
            this.closeDialog()
            // No cameras found. Please ensure your device has a camera and camera access is allowed
            alert(
              'ไม่พบกล้อง โปรดตรวจสอบว่าอุปกรณ์ของคุณมีกล้องและอนุญาตให้เข้าถึงกล้อง',
            )
          }
        } catch {
          this.closeDialog()
          //   Failed to start the scanner. Please ensure camera access is allowed.
          alert(
            'ไม่สามารถเริ่มสแกนได้ โปรดตรวจสอบสิทธิ์การเข้าถึงกล้อง',
          )
        }
      } else {
        // Failed to start the scanner. Please ensure camera access is allowed.
        alert(
          'ไม่สามารถเริ่มสแกนได้ โปรดตรวจสอบสิทธิ์การเข้าถึงกล้อง',
        )
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('pageshow', this.handlePageShow)

    // Ensure to cleanup and stop the scanner when the component is destroyed
    // (forceCloseScanner() may have already stopped and nulled the instance).
    if (this.isHtml5Barcode && this.html5Barcode) {
      this.html5Barcode.stop().catch(() => {
        // Unable to stop scanning
        alert('เกิดข้อผิดพลาดในการหยุดการสแกน')
      })
    }
  },
}
</script>

<style lang="stylus" scoped>
#bar-code-reader {
  width: 100%;
  height: 100%;

  max-width: 100dvw;
  max-height: 100dvh;
}
</style>
